import Button from './ui/Button';
import Icon from './ui/Icon';

const assurances = [
  'Free project estimate',
  'Reply within 24 hours',
  'Websites, apps & AI under one roof',
];

/* Illustrative snippet showing what an AI integration looks like in a Next.js app. */
const CodeCard = () => (
  <div
    aria-hidden="true"
    className="relative z-20 -mt-10 ml-3 mr-3 overflow-hidden rounded-xl border border-white/10 bg-ink-900/95 shadow-glow backdrop-blur-md sm:absolute sm:-left-8 sm:bottom-2 sm:m-0 sm:w-[336px] sm:animate-float"
  >
    <div className="flex items-center gap-2 border-b border-white/[0.08] px-3.5 py-2">
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
      <span className="font-mono text-[10.5px] text-subtle">app/api/assistant/route.ts</span>
    </div>
    <pre className="overflow-hidden px-3.5 py-3 font-mono text-[10.5px] leading-[1.7] text-muted sm:text-[11px]">
      <span className="text-violet-300">export async function</span> <span className="text-sky-300">POST</span>(req: Request) {'{'}
      {'\n'}
      {'  '}
      <span className="text-violet-300">const</span> {'{ messages }'} = <span className="text-violet-300">await</span> req.<span className="text-sky-300">json</span>();
      {'\n\n'}
      {'  '}
      <span className="text-violet-300">const</span> {'{ choices }'} = <span className="text-violet-300">await</span> openai.chat
      {'\n'}
      {'    '}.completions.<span className="text-sky-300">create</span>({'{'} model, messages {'}'});
      {'\n\n'}
      {'  '}
      <span className="text-violet-300">return</span> Response.<span className="text-sky-300">json</span>(choices[0].message);
      {'\n'}
      {'}'}
    </pre>
  </div>
);

const StackCard = () => (
  <div
    aria-hidden="true"
    className="absolute -right-3 top-16 z-20 hidden animate-float flex-col gap-2 rounded-xl border border-white/10 bg-ink-900/90 p-3.5 shadow-glow backdrop-blur-md [animation-delay:1.2s] sm:flex lg:-right-6"
  >
    <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-subtle">Built with</span>
    {[
      ['Next.js', 'bg-white'],
      ['TypeScript', 'bg-sky-400'],
      ['OpenAI API', 'bg-emerald-400'],
    ].map(([label, dot]) => (
      <span key={label} className="flex items-center gap-2 text-xs font-medium text-fg">
        <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
        {label}
      </span>
    ))}
  </div>
);

const HeroVisual = () => (
  <div className="relative mx-auto w-full max-w-[600px] sm:pb-12 lg:max-w-none">
    <div
      className="absolute -inset-8 -z-10 rounded-[48px] opacity-90 blur-2xl"
      style={{
        background:
          'radial-gradient(60% 60% at 60% 30%, rgba(99,102,241,0.32), transparent 70%), radial-gradient(40% 40% at 20% 80%, rgba(34,211,238,0.12), transparent 70%)',
      }}
      aria-hidden="true"
    />
    <div className="overflow-hidden rounded-2xl border border-white/10 bg-ink-800 shadow-glow">
      <div className="flex h-9 items-center gap-2 border-b border-white/[0.08] bg-ink-700/70 px-4">
        <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
        <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
        <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
        <span className="ml-3 min-w-0 flex-1 truncate rounded-md bg-black/30 px-3 py-1 font-mono text-[11px] text-subtle">
          linkneat.com
        </span>
      </div>
      <img
        src="/images/projects/linkneat-1280.webp"
        srcSet="/images/projects/linkneat-720.webp 720w, /images/projects/linkneat-1280.webp 1280w"
        sizes="(min-width: 1024px) 600px, (min-width: 640px) 600px, 92vw"
        width="1280"
        height="800"
        alt="Linkneat Career & Tax Services homepage, a multilingual business website built by KBL Web Solutions"
        className="block h-auto w-full"
        decoding="async"
        fetchpriority="high"
      />
    </div>
    <StackCard />
    <CodeCard />
  </div>
);

const Hero = () => (
  <section id="top" className="relative overflow-hidden pt-28 pb-16 sm:pt-32 lg:pt-36 lg:pb-24" aria-labelledby="hero-title">
    <div className="pointer-events-none absolute inset-0" aria-hidden="true">
      <div className="bg-grid absolute inset-0" />
      <div
        className="absolute -top-40 right-[-10%] h-[720px] w-[720px] rounded-full opacity-80"
        style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.22), rgba(139,92,246,0.08) 45%, transparent 68%)' }}
      />
    </div>

    <div className="container-page relative grid items-center gap-14 lg:grid-cols-[1.02fr_1fr] lg:gap-10">
      <div>
        <p className="inline-flex items-center gap-2.5 rounded-full border border-white/10 bg-white/[0.04] py-1.5 pl-3 pr-4 text-xs font-medium text-muted">
          <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-emerald-400" aria-hidden="true" />
          KBL Web Solutions · Bay Area, California
        </p>

        <h1
          id="hero-title"
          className="mt-6 text-[2.4rem] font-semibold leading-[1.1] tracking-tight text-fg [text-wrap:balance] sm:text-5xl lg:text-[3.6rem]"
        >
          Modern Websites, Web Apps &amp; <span className="text-gradient">AI Solutions</span> for Growing Businesses
        </h1>

        <p className="mt-6 max-w-xl text-base leading-relaxed text-muted sm:text-lg">
          I’m Mirwes, and I build modern websites, custom web applications and AI-powered tools for small and growing
          businesses, helping you strengthen your online presence and streamline the way you work.
        </p>

        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
          <Button href="#contact" size="lg" arrow>
            Start a Project
          </Button>
          <Button href="#work" size="lg" variant="secondary">
            View My Work
          </Button>
        </div>

        <ul className="mt-8 flex flex-col gap-2.5 text-sm text-muted sm:flex-row sm:flex-wrap sm:gap-x-6">
          {assurances.map((item) => (
            <li key={item} className="flex items-center gap-2">
              <Icon name="Check" className="h-4 w-4 text-emerald-400" strokeWidth={2.5} />
              {item}
            </li>
          ))}
        </ul>
      </div>

      <HeroVisual />
    </div>
  </section>
);

export default Hero;
